import { memo, useId, useState } from 'react';
import type { BillDTO, PaymentListItemDTO } from '@six-pockets/shared';
import { ChevronDownIcon, EyeIcon } from '@/components/icons';
import { Avatar, Button } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatClock, formatINR, formatPhone } from '@/utils/format';
import { billContents, billParty, paymentKindLabel } from '../utils';
import styles from '@/styles/components/PaymentRow/style.scss';

const METHOD_LABEL: Record<PaymentListItemDTO['method'], string> = { upi: 'UPI', cash: 'Cash', split: 'Split' };

const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

interface PaymentRowProps {
  payment: PaymentListItemDTO;
  onView: (bill: BillDTO) => void;
}

/**
 * One payment on the Payments page: customer (time · how it came in · bill numbers) · payment mode · amount · View
 * bill. A payment that went to several bills (a Pay now) has one "N bills" button instead, which opens the bills
 * under the row (number · day billed · what · its share of the payment · View), so the row keeps its height.
 */
export const PaymentRow = memo(function PaymentRow({ payment: p, onView }: PaymentRowProps) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const first = p.bills[0];
  const name = p.customer?.name ?? (first ? billParty(first) : 'Walk-in');
  const kind = first ? paymentKindLabel(p.kind, p.paidAt, first.createdAt) : '';
  const several = p.bills.length > 1;

  return (
    <li className={cx(styles.row, open && styles.open)}>
      <div className={styles.head}>
        <Avatar name={name} tone={p.customer ? 'green' : 'neutral'} />
        <div className={styles.main}>
          <div className={styles.titleRow}>
            <span className={cx(styles.name, !p.customer && styles.noCustomer)}>{name}</span>
            {p.customer && <span className={styles.phone}>{formatPhone(p.customer.phone)}</span>}
          </div>
          <span className={styles.meta}>
            {formatClock(p.paidAt)} · {kind}
            {p.bills.length > 0 && <> · {p.bills.map((b) => `#${b.billNumber}`).join(', ')}</>}
          </span>
        </div>

        <span className={styles.mode}>
          <span className={styles.label}>Mode</span>
          <span className={styles.method}>{METHOD_LABEL[p.method]}</span>
          {p.method === 'split' && (
            <span className={styles.note}>
              UPI {formatINR(p.upi)} · Cash {formatINR(p.cash)}
            </span>
          )}
        </span>

        <span className={styles.payment}>
          <span className={styles.label}>Payment</span>
          <span className={styles.amount}>{formatINR(p.amount)}</span>
          {p.discount > 0 && <span className={cx(styles.note, styles.discount)}>{formatINR(p.discount)} discount</span>}
        </span>

        <div className={styles.view}>
          {several ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<ChevronDownIcon size={15} className={styles.chevron} />}
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => setOpen((o) => !o)}
            >
              {p.bills.length} bills
            </Button>
          ) : (
            first && (
              <Button size="sm" variant="secondary" icon={<EyeIcon size={15} />} onClick={() => onView(first)} aria-label={`View bill ${first.billNumber}`}>
                View bill
              </Button>
            )
          )}
        </div>
      </div>

      {several && open && (
        <ul id={listId} className={styles.bills}>
          {p.bills.map((b, i) => (
            <li key={b.id} className={styles.bill}>
              <span className={styles.number}>#{b.billNumber}</span>
              <span className={styles.billMain}>
                <span className={styles.billDay}>Billed {shortDay(b.createdAt)}</span>
                <span className={styles.contents}>{billContents(b)}</span>
              </span>
              <span className={styles.share}>
                <b>{formatINR(p.shares[i] ?? 0)}</b> of {formatINR(p.amount)}
              </span>
              <Button size="sm" variant="ghost" icon={<EyeIcon size={15} />} onClick={() => onView(b)} aria-label={`View bill ${b.billNumber}`}>
                View
              </Button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
});
